/**
 * @file BlendSpecModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-14
 * @modified 2026-08-14
 * @description Create or edit a nutrient target spec.
 *
 *              A spec is a named set of bands — a min, a max, or both — against
 *              the fresh-basis nutrients the calculator computes. Every nutrient
 *              gets a row whether or not it's targeted; leaving both boxes empty
 *              means "no target", which is why the rows aren't added and removed
 *              one at a time.
 *
 *              A min above its max is rejected on save rather than stored and
 *              quietly failing every blend it's ever attached to.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import Dropdown from './Dropdown';
import { useSlideOutButton } from './SlideOutButton';
import { BLEND_TYPE_OPTIONS } from '../utils/blendTypes';
import './BlendCalculator.css';

/** targets JSONB → the flat {key: {min, max}} the form edits, as strings. */
function toForm(targets) {
  const out = {};
  for (const [key, t] of Object.entries(targets || {})) {
    out[key] = {
      min: t?.min === null || t?.min === undefined ? '' : String(t.min),
      max: t?.max === null || t?.max === undefined ? '' : String(t.max)
    };
  }
  return out;
}

/**
 * The form back to targets JSONB. A bound is only written when it parses as a
 * number, so a half-typed "1." never reaches the database, and a nutrient with
 * neither bound is dropped entirely rather than stored as an empty object that
 * evaluateSpec would treat as a target that everything passes.
 */
function toTargets(form) {
  const out = {};
  for (const [key, t] of Object.entries(form)) {
    const min = Number(t.min);
    const max = Number(t.max);
    const hasMin = t.min !== '' && Number.isFinite(min);
    const hasMax = t.max !== '' && Number.isFinite(max);
    if (!hasMin && !hasMax) continue;
    out[key] = {};
    if (hasMin) out[key].min = min;
    if (hasMax) out[key].max = max;
  }
  return out;
}

function SpecBody({ name, setName, blendType, setBlendType, description, setDescription,
                    components, form, setBound, error, targeted }) {
  useSlideOutButton([targeted]);

  return (
    <div className="blend-spec-body">
      {error && (
        <div className="blend-warning inline">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}

      <div className="blend-spec-head">
        <div className="filter-group">
          <label htmlFor="bs-name">Name *</label>
          <Input id="bs-name" type="text" value={name}
                 placeholder="e.g. TMB 18"
                 onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="filter-group">
          <label>Blend type</label>
          <Dropdown options={BLEND_TYPE_OPTIONS} value={blendType} onChange={setBlendType} />
        </div>
      </div>

      <div className="filter-group">
        <label htmlFor="bs-desc">Description</label>
        <Input id="bs-desc" type="text" value={description}
               placeholder="What this spec is for — optional"
               onChange={(e) => setDescription(e.target.value)} />
      </div>

      <p className="blend-spec-hint">
        Set a minimum, a maximum, or both. Leave a row empty and that nutrient
        isn't targeted. A blend is only in spec when every targeted nutrient sits
        inside its band.
      </p>

      <table className="data-table blend-spec-table">
        <thead>
          <tr>
            <th>Nutrient</th>
            <th className="num">Min</th>
            <th className="num">Max</th>
          </tr>
        </thead>
        <tbody>
          {components.map((c) => {
            const row = form[c.key] || { min: '', max: '' };
            const on = row.min !== '' || row.max !== '';
            return (
              <tr key={c.key} className={on ? 'targeted' : ''}>
                <td>
                  {c.label}
                  {c.unit ? <span className="blend-spec-unit">{c.unit}</span> : null}
                </td>
                <td className="num">
                  <Input type="number" inputMode="decimal" value={row.min}
                         placeholder="—"
                         onChange={(e) => setBound(c.key, 'min', e.target.value)} />
                </td>
                <td className="num">
                  <Input type="number" inputMode="decimal" value={row.max}
                         placeholder="—"
                         onChange={(e) => setBound(c.key, 'max', e.target.value)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="blend-spec-summary">
        {targeted} of {components.length} nutrients targeted.
      </p>
    </div>
  );
}

export default function BlendSpecModal({ spec, onClose, onSaved }) {
  const [name, setName] = useState(spec?.name || '');
  const [blendType, setBlendType] = useState(spec?.blend_type || '');
  const [description, setDescription] = useState(spec?.description || '');
  const [form, setForm] = useState(() => toForm(spec?.targets));
  const [components, setComponents] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/blends/nutrients`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { components: [] }))
      .then((d) => { if (!cancelled) setComponents(d.components || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setBound = useCallback((key, which, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { min: '', max: '' }), [which]: value }
    }));
  }, []);

  const targeted = useMemo(
    () => Object.values(form).filter((t) => t.min !== '' || t.max !== '').length, [form]);

  const save = useCallback(async () => {
    if (!name.trim()) { setError('Give the spec a name.'); return; }

    const targets = toTargets(form);
    // A band that can never be met is a silent trap — every blend attached to it
    // would fail with no indication why. Catch it here.
    const bad = Object.entries(targets).find(
      ([, t]) => t.min !== undefined && t.max !== undefined && t.min > t.max);
    if (bad) {
      const label = components.find((c) => c.key === bad[0])?.label || bad[0];
      setError(`${label}: the minimum is above the maximum. No blend could ever meet that.`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await fetch(
        spec?.id ? `${API_URL}/api/blends/specs/${spec.id}` : `${API_URL}/api/blends/specs`,
        {
          method: spec?.id ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: name.trim(),
            blend_type: blendType || null,
            description: description.trim() || null,
            targets
          })
        });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      onSaved(data.spec, spec?.id ? 'updated' : 'created');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [name, blendType, description, form, spec, components, onSaved]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={spec?.id ? `Edit ${spec.name}` : 'New target spec'}
      size="md"
      className="blend-spec"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-round" onClick={save} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>
      }
    >
      <SpecBody
        name={name} setName={setName}
        blendType={blendType} setBlendType={setBlendType}
        description={description} setDescription={setDescription}
        components={components} form={form} setBound={setBound}
        error={error} targeted={targeted}
      />
    </Modal>
  );
}
